import { Project } from 'lib/types/projects';
import { imagePostUrl } from 'lib/utils/imageUtils';
import { abbreviateNum, buildProjectPath } from 'lib/helpers';
import { ReactNode } from 'react';
import BurstModeIcon from '@mui/icons-material/BurstMode';
import { metrics } from 'components/PostMetrics/helpers';
import {
  Paper, CardMedia, Chip,
} from '@mui/material';
import Link from 'next/link';

const METRICS: Record<string, { icon: ReactNode; label: string }> = {
  ...metrics,
  posts: {
    icon: <BurstModeIcon fontSize="small" />,
    label: 'posts',
  },
};

interface Props {
  project: Project;
  showUsername?: boolean;
}

export const PortfolioCard = ({ project, showUsername = false }: Props) => {
  let coverImg = project.coverImgUrl || '';
  if (!coverImg && project.posts?.find((post) => post.thumbnailUrl)) {
    coverImg = imagePostUrl(project.posts?.find((post) => post.thumbnailUrl)?.thumbnailUrl || '', {
      width: 600,
    });
  }

  const metricIcon = (key: string) => {
    const metric = METRICS[key];
    if (!metric) return null;
    return metric.icon;
  };

  const getPreviewDescription = (description: string) => {
    const limit = 250;
    let descriptionPreview = description?.trim() || '';
    if (descriptionPreview.length <= limit) {
      return descriptionPreview;
    }

    const cutText = descriptionPreview.slice(0, limit);
    const lastSpaceIndex = cutText.lastIndexOf(' ');
    descriptionPreview = `${cutText.slice(0, lastSpaceIndex)}`;

    if (descriptionPreview[descriptionPreview.length - 1].match(/\W|[_]/g)) {
      descriptionPreview = descriptionPreview.slice(0, descriptionPreview.length - 1);
    }

    return `${descriptionPreview}...`;
  };

  const projectPath = buildProjectPath(project);

  return (
    <Link href={projectPath}>
      <Paper key={project.id} elevation={6}>
        <div
          className={`relative flex items-center ${coverImg ? 'text-white' : 'text-black dark:text-white min-h-[250px] md:min-h-[200px] rounded-md dark:bg-darkgray'}`}
        >
          <div className="z-10">
            <div className="absolute right-1 top-1 rounded-xl text-white z-10">
              {showUsername ? (
                <Chip
                  label={project.owner
                    ? <Link href={`https://gondola.cc/${project.owner.username}`}>{project.owner.name}</Link>
                    : project.team?.name}
                  sx={{ color: 'white', backgroundColor: '#0E8C8D' }}
                />
              ) : (
                <Chip label="Project" size="small" sx={{ color: 'white', backgroundColor: 'rgba(0, 0, 0, 0.5)' }} />
              )}
            </div>
            <div
              className="absolute left-0 top-0 text-shadow-lg/30 p-3 w-full h-full rounded-xl"
              style={{ backgroundImage: coverImg && 'linear-gradient(to top, #2c2c2c, #00000010 20%, #00000010 70%, #2c2c2c 90%)' }}
            >
              <p className="text-2xl font-bold">
                {project.name}
              </p>
              <p className="text-sm mt-4">
                {!coverImg && getPreviewDescription(project.description || '')}
              </p>
            </div>

            <div className="absolute left-1 bottom-0 p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs mt-1">
                <div className="flex items-center gap-1">
                  {metricIcon('posts')}
                  <span>{abbreviateNum(project.stats?.totalPosts || 0, 0)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {metricIcon('views')}
                  <span>{abbreviateNum(project.stats?.totalViews || 0, 0)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {metricIcon('likes')}
                  <span>{abbreviateNum(project.stats?.totalLikes || 0, 0)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {metricIcon('shares')}
                  <span>{abbreviateNum(project.stats?.totalShares || 0, 0)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {metricIcon('comments')}
                  <span>{abbreviateNum(project.stats?.totalComments || 0, 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {coverImg && (
            <div className="relative rounded-xl w-full min-w-[300px] max-h-[300px] overflow-hidden">
              <CardMedia
                component="img"
                alt="Project cover image"
                image={coverImg}
                suppressHydrationWarning
              />
            </div>
          )}
        </div>
      </Paper>
    </Link>
  );
};
